/**
 * The reading layout of the two policy pages of the Sept 2026 redesign: Privacy
 * (PrivacySlot.jsx) and Terms (TermsSlot.jsx). Ports the package's `.page-fine` and
 * `.legal-layout` (site/pages.css + site/flair.css): a "Last updated" line under the hero,
 * then a table of contents (lilac panel, sticky beside the text from the 800px nav
 * breakpoint, two columns above the text on tablets, one on phones) and the policy in a
 * 740px reading column, one ruled-off section per heading.
 *
 * These parts hold no policy wording: every heading, sentence, list item and date comes
 * from the pages (their exported *_SECTIONS / *_LAST_UPDATED constants), which the owner
 * signs off. Layout only.
 */
import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Heading, SiteContainer } from '@/components/site';

// The package's second breakpoint is 1050px (pages.css `max-width:1050px`): `min-[1051px]:`,
// its phone breakpoint 480px (`max-width:480px`): `min-[481px]:`.

/**
 * The table of contents' panel: lilac 55% into sheet (the package's pale lilac TOC panel). Contrast of
 * the text on it: brand-muted 5.16:1, navy 13.23:1; the rust focus ring 5.25:1. The border
 * is lilac 94% into navy (the package's darker lilac edge; decorative).
 */
const TOC_TINT = 'bg-[color-mix(in_srgb,theme(colors.brand.lilac)_55%,theme(colors.brand.sheet))]';
const TOC_BORDER = 'border-[color:color-mix(in_srgb,theme(colors.brand.lilac)_94%,theme(colors.navy.DEFAULT))]';

/**
 * Where a section lands when the contents link (or a deep link) jumps to it: 115px from the
 * top at 16px text, below the fixed header (78px tall at 17px). ScrollToTop reads the same
 * margin. In rem, so it clears the header when the text is enlarged too (230px at 32px; the
 * header then ends at 146px).
 */
export const LEGAL_ANCHOR_OFFSET = 'scroll-mt-[7.1875rem]';

/** An inline link inside the policy text (navy, underlined; rust on hover). */
export const LEGAL_LINK = 'text-navy underline decoration-1 underline-offset-[3px] transition-colors duration-180 hover:text-brand-rust';

const BODY_TEXT = 'text-[1.0625rem] leading-[1.8] text-brand-muted';

/**
 * "Last updated: <date>" under the hero (the package's `.page-fine`), in a <time>. It sits
 * 48px below PageHero, as in the reference: the hero's (empty) actions row adds 30px there,
 * then the line's own 18px. Props: `label` (as shown), `iso` (the <time> dateTime).
 * Nothing without a label.
 */
export function LegalUpdated({ label, iso }) {
  if (!label) return null;
  return (
    <SiteContainer>
      <p className="mb-[13px] mt-12 max-w-[66ch] text-[0.8125rem] leading-[1.65] text-brand-muted">
        Last updated: <time dateTime={iso}>{label}</time>
      </p>
    </SiteContainer>
  );
}

/**
 * The table of contents: a <nav> named "On this page" with one link per section (`#id`).
 * One column on phones, two from 481px, a sticky column beside the text from 800px (it
 * scrolls itself if the window is too short to show it whole). The sticky offset is in rem
 * (115px at 16px text, 230px at 32px), so the panel stays below the fixed header, which
 * grows with enlarged text; its height leaves 15px (0.9375rem) under it. Links are at least 44px tall.
 * The section being read is marked (aria-current="location": navy, semibold, a rust rule).
 */
/**
 * The section being read (owner upgrade brief 2026-09-27 §3.15): the last section whose top
 * has passed a line just under the fixed header. IntersectionObserver only (none: no
 * highlight); '' before the first section.
 */
export function useActiveSection(ids) {
  const [active, setActive] = useState('');
  const key = ids.join('|');
  useEffect(() => {
    const IO = typeof window !== 'undefined' ? window.IntersectionObserver : undefined;
    if (typeof IO !== 'function' || !ids.length) return undefined;
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return undefined;
    const io = new IO(() => {
      // Pick from the live positions (the entries only say what changed).
      const line = 140;
      let current = '';
      els.forEach((el) => {
        if (el.getBoundingClientRect().top <= line) current = el.id;
      });
      setActive(current);
    }, { rootMargin: '-130px 0px -55% 0px', threshold: [0, 1] });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [key]);
  return active;
}

export function LegalContents({ sections = [], label = 'On this page' }) {
  const active = useActiveSection(sections.map((s) => s.id));
  if (!sections.length) return null;
  return (
    <nav
      aria-label={label}
      className={cn(
        'site-light self-start border p-5 nav:sticky nav:top-[7.1875rem] nav:max-h-[calc(100vh-8.125rem)] nav:overflow-y-auto print:static print:max-h-none',
        TOC_TINT,
        TOC_BORDER,
      )}
    >
      <ul role="list" className="grid min-[481px]:grid-cols-2 min-[481px]:gap-x-[18px] nav:block">
        {sections.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              aria-current={active === s.id ? 'location' : undefined}
              className={cn(
                'flex min-h-11 items-center py-[9px] text-sm leading-[1.55] text-brand-muted underline-offset-4 transition-colors duration-180 hover:text-navy hover:underline',
                // The section being read: navy, semibold, a rust rule on its left (from 800px).
                'aria-[current=location]:font-semibold aria-[current=location]:text-navy nav:-ml-5 nav:border-l-[3px] nav:border-transparent nav:pl-[17px] nav:aria-[current=location]:border-brand-rust',
              )}
            >
              {s.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * The policy text: one <section id> per entry, named by its h2, ruled off below. The h2 is
 * 36px on phones, 32px from 481px and 34px from 800px (the package's `.legal-copy h2`); the
 * text 17px / 1.8 in brand-muted (5.62:1 on paper). A contents link moves focus to its
 * section (ScrollToTop gives it tabindex="-1"): it takes focus without a ring, like the
 * skip link's <main> (it is not a control; the next Tab continues from there).
 *
 * Props: `sections` [{ id, title, body: node[], points?: node[], after?: node[] }] —
 * paragraphs, then a bulleted list, then paragraphs after it.
 */
export function LegalSections({ sections = [] }) {
  return (
    <article className="min-w-0 max-w-[46.25rem]">
      {sections.map((s) => (
        <section
          key={s.id}
          id={s.id}
          aria-labelledby={`${s.id}-title`}
          className={cn('border-b border-brand-rule pb-5 focus:outline-none', LEGAL_ANCHOR_OFFSET)}
        >
          <Heading
            level={2}
            size="minor"
            id={`${s.id}-title`}
            className="mb-6 mt-[45px] text-[2.25rem] leading-[1.06] tracking-[-0.025em] min-[481px]:text-[2rem] nav:text-[2.125rem]"
          >
            {s.title}
          </Heading>
          {(s.body || []).map((p, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={i} className={cn('my-[1em]', BODY_TEXT)}>
              {p}
            </p>
          ))}
          {s.points && s.points.length > 0 ? (
            <ul className={cn('my-4 list-disc pl-10', BODY_TEXT)}>
              {s.points.map((p, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>{p}</li>
              ))}
            </ul>
          ) : null}
          {(s.after || []).map((p, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={`after-${i}`} className={cn('my-[1em]', BODY_TEXT)}>
              {p}
            </p>
          ))}
        </section>
      ))}
    </article>
  );
}

/**
 * Everything under a policy page's hero: the "Last updated" line, then the contents and
 * the text side by side (240px + 75px gap from 1051px, 200px + 40px from 800px, stacked
 * with a 35px gap below). Props: `lastUpdated` { label, iso }, `sections` (as above).
 */
export function LegalBody({ lastUpdated, sections = [] }) {
  return (
    <>
      <LegalUpdated label={lastUpdated && lastUpdated.label} iso={lastUpdated && lastUpdated.iso} />
      <SiteContainer
        className={cn(
          'grid gap-[35px] pb-[90px] print:block',
          'nav:grid-cols-[200px_minmax(0,1fr)] nav:gap-10',
          'min-[1051px]:grid-cols-[240px_minmax(0,1fr)] min-[1051px]:gap-[75px]',
        )}
      >
        <LegalContents sections={sections} />
        <LegalSections sections={sections} />
      </SiteContainer>
    </>
  );
}
